import React, {useState} from 'react';

import {updateAnalysisOptimistically} from 'analysis-tool/AnalysisActions';
import {AnalysisGraphQL} from 'analysis-tool/AnalysisTypes';
import BenchlingConfig from 'BenchlingConfig';
import FolderSelector from 'file-browser/FolderSelector';
import EditableStudiesList from 'studies/studyable/EditableStudiesList';
import Col from 'ui-library/bs/Col';
import FormGroup from 'ui-library/bs/FormGroup';
import Row from 'ui-library/bs/Row';
import {ButtonStyle} from 'ui-library/buttons/Button';
import useFormInput from 'ui-library/hooks/useFormInput';
import useFuzzyDate from 'ui-library/hooks/useFuzzyDate';
import FieldLabel from 'ui-library/inputs/FieldLabel';
import TextField from 'ui-library/inputs/TextField';
import Modal from 'ui-library/modals/Modal';
import {readableTime} from 'util/DateUtil';
import {capitalize} from 'util/NameUtil';

import DataSharingWarningBanner from '../components/DataSharingWarningBanner';
import AnalysisRelevantItemsSection from './AnalysisRelevantItemsSection';

export interface AnalysisMetadataModalProps {
  onRequestClose: () => void;
  onRequestSubmit: () => void;
  analysis: AnalysisGraphQL;
}

export default function AnalysisMetadataModal(props: AnalysisMetadataModalProps): JSX.Element {
  const nameFormInput = useFormInput(props.analysis.name);
  const descriptionFormInput = useFormInput(props.analysis.description ?? '');
  const [folderId, setFolderId] = useState(props.analysis.folder.id);

  const name = nameFormInput.value;
  const canSubmit = name && name.match(/^\s*$/) == null;
  const writable = props.analysis.permissions.writable;

  function handleSubmit(): void {
    if (writable) {
      const serverDescription = props.analysis.description ?? '';
      updateAnalysisOptimistically(props.analysis, {
        name: nameFormInput.value,
        folderId,
        ...(descriptionFormInput.value !== serverDescription
          ? {description: descriptionFormInput.value}
          : {}),
      });
    }
    props.onRequestSubmit();
  }

  return (
    <Modal
      title="Analysis metadata"
      buttons={[
        {style: ButtonStyle.SECONDARY, submit: true, text: writable ? 'Save' : 'Done', disabled: !canSubmit},
      ]}
      onRequestClose={props.onRequestClose}
      onRequestSubmit={handleSubmit}
      expandHeight={true}
    >
      <Row>
        <Col sm={6}>
          <FormGroup>
            <TextField
              label="Analysis name"
              disabled={!writable}
              autoFocus={true}
              {...nameFormInput}
              dataTestAttrs={{component: 'AnalysisMetadataModal', element: 'TextField', key: 'name'}}
            />
          </FormGroup>
        </Col>
        <Col sm={6}>
          <FormGroup>
            <FieldLabel label="Folder" />
            <FolderSelector
              enableRedesign={true}
              readOnly={!writable}
              showNotebook={true}
              value={folderId}
              onChange={(e: {target: {value: string}}) => {
                setFolderId(e.target.value);
              }}
              unsetOnFetchFail={true}
            />
          </FormGroup>
        </Col>
      </Row>
      {folderId !== props.analysis.folder.id && (
        <>
          <Row>
            <Col sm={12}>
              <DataSharingWarningBanner folderId={folderId} />
            </Col>
          </Row>
          <br />
        </>
      )}
      <Row>
        <Col sm={12}>
          <TextField
            label="Description"
            disabled={!writable}
            {...descriptionFormInput}
            dataTestAttrs={{component: 'AnalysisMetadataModal', element: 'TextField', key: 'description'}}
          />
        </Col>
      </Row>
      <br />
      <Row>
        <Col sm={6}>
          <FormGroup>
            <FieldLabel label="Creator" />
            <p>{props.analysis.creator.name}</p>
          </FormGroup>
        </Col>
        <Col sm={6}>
          <FormGroup>
            <FieldLabel label="Last modified" />
            <p>{capitalize(useFuzzyDate(props.analysis.modifiedAt))}</p>
          </FormGroup>
        </Col>
      </Row>
      <Row>
        <Col sm={6}>
          <FormGroup>
            <FieldLabel label="Created at" />
            <p>{readableTime(props.analysis.createdAt)}</p>
          </FormGroup>
        </Col>
        {BenchlingConfig.STUDIES_ENABLE && (
          <Col sm={6}>
            <FieldLabel label="Studies" />
            <EditableStudiesList studyable={props.analysis} isWritable={writable} />
          </Col>
        )}
      </Row>
      <Row>
        <Col sm={6}>
          <FormGroup>
            <FieldLabel label="Step count" />
            <p>
              {props.analysis.steps.length} / {BenchlingConfig.PIPELINES_MAX_STEP_COUNT}
            </p>
          </FormGroup>
        </Col>
      </Row>
      <hr />
      <Row>
        <Col sm={12}>
          <FormGroup>
            <FieldLabel label="Relevant items" />
            <AnalysisRelevantItemsSection analysisId={props.analysis.id} />
          </FormGroup>
        </Col>
      </Row>
    </Modal>
  );
}
